// Payroll Settings client — company-policy configuration that lives behind
// payroll-service but had no UI: employer legal entities + statutory numbers,
// per-employee payroll profiles (statutory IDs, nature/residential/employment
// type, hire date, basic pay), and payslip branding. Talks via the gateway.
import { gatewayApi } from '@/lib/hrApi';
import type { EmployeeProfileSchema } from '@/features/payroll/types/employeeProfileSchema';

const BASE = '/api/v1';

export interface LegalEntity {
  id: string;
  name: string;
  country: string;
  employer_tin: string;
  base_currency: string;
  employer_statutory_ids?: Record<string, string>;
}

export interface EmployeeProfile {
  employee_id: string;
  full_name: string;
  email: string;
  national_id: string;
  legal_entity_id: string | null;
  statutory_ids: Record<string, string>;
  heslb_beneficiary: boolean;
  nature_of_employment: string;
  residential_status: string;
  employment_type: string;
  hire_date: string | null;
  basic_salary: string | null;
  currency: string | null;
}

export interface AssignmentInput {
  legal_entity_id?: string;
  statutory_ids?: Record<string, string>;
  heslb_beneficiary?: boolean;
  national_id?: string;
  nature_of_employment?: string;
  residential_status?: string;
  employment_type?: string;
  hire_date?: string;
}

// --- legal entities (employer filing identity) ---
// Pay schedule (per legal entity): pay day + cutoffs, weekend adjustment.
export interface PaySchedule {
  version: number;
  pay_day: number;
  attendance_cutoff_day: number;
  input_cutoff_day: number;
  adjust_weekends: boolean;
}
export const getPaySchedule = (entityId: string) =>
  gatewayApi.get<PaySchedule>(`${BASE}/legal-entities/${entityId}/pay-schedule`).then((r) => r.data);
export const setPaySchedule = (entityId: string, body: PaySchedule) =>
  gatewayApi.put<PaySchedule>(`${BASE}/legal-entities/${entityId}/pay-schedule`, body).then((r) => r.data);

export const listLegalEntities = () =>
  gatewayApi.get<{ legal_entities: LegalEntity[] }>(`${BASE}/legal-entities`).then((r) => r.data.legal_entities ?? []);

export const createLegalEntity = (body: {
  name: string;
  country: string;
  employer_tin?: string;
  employer_statutory_ids?: Record<string, string>;
  base_currency?: string;
}) => gatewayApi.post<{ id: string }>(`${BASE}/legal-entities`, body).then((r) => r.data);

export const updateLegalEntity = (
  id: string,
  body: {
    name: string;
    country: string;
    employer_tin?: string;
    employer_statutory_ids?: Record<string, string>;
    base_currency?: string;
  },
) => gatewayApi.put<{ id: string }>(`${BASE}/legal-entities/${id}`, body).then((r) => r.data);

export const deleteLegalEntity = (id: string) =>
  gatewayApi.delete(`${BASE}/legal-entities/${id}`).then((r) => r.data);

// --- country-pack-driven employee identifier schema (drives the profile form) ---
// Returns the statutory-id field definitions for the given country's active pack.
// The form renders identity fields from this, so a new country needs no React change.
// A 404 (country_unsupported) means no pack is published — the caller falls back.
export const getEmployeeProfileSchema = (countryCode: string, asOf?: string) =>
  gatewayApi
    .get<EmployeeProfileSchema>(
      `${BASE}/payroll/countries/${encodeURIComponent(countryCode)}/employee-profile-schema`,
      { params: asOf ? { asOf } : undefined },
    )
    .then((r) => r.data);

// --- per-employee payroll profile ---
export const getEmployeeProfile = (employeeId: string) =>
  gatewayApi.get<EmployeeProfile>(`${BASE}/payroll/employees/${employeeId}`).then((r) => r.data);

export const saveAssignment = (employeeId: string, body: AssignmentInput) =>
  gatewayApi.post(`${BASE}/payroll/employees/${employeeId}/assignment`, body).then((r) => r.data);

export const setCompensation = (employeeId: string, body: { amount: string; currency: string; effective_from: string }) =>
  gatewayApi.post(`${BASE}/payroll/employees/${employeeId}/compensation`, body).then((r) => r.data);

// --- per-employee payroll profile (Increment B3: inclusion + statutory flags) ---
export type PayFrequency = 'monthly' | 'weekly' | 'biweekly' | 'daily' | 'ad_hoc';
export type PayType = 'salary' | 'hourly' | 'daily' | 'piece_rate' | 'commission' | 'stipend';
export type SdlIncluded = 'yes' | 'no' | 'auto';

export interface PayrollProfile {
  tenant_id?: string;
  employee_id: string;
  payroll_enabled: boolean;
  payroll_group?: string | null;
  pay_frequency?: PayFrequency | string | null;
  pay_type?: PayType | string | null;
  paye_applicable?: boolean;
  nssf_applicable?: boolean;
  wcf_applicable?: boolean;
  sdl_included?: SdlIncluded | string | null;
  health_insurance_applicable?: boolean | null;
  wcf_risk_category?: string | null;
  statutory_exemption_reason?: string | null;
}

export const getPayrollProfile = (employeeId: string) =>
  gatewayApi.get<PayrollProfile>(`${BASE}/payroll/employees/${employeeId}/payroll-profile`).then((r) => r.data);

// Partial body; the backend 400s with code `exemption_reason_required` when a
// statutory scheme is switched off without a statutory_exemption_reason.
export const setPayrollProfile = (employeeId: string, patch: Partial<PayrollProfile>) =>
  gatewayApi.put<PayrollProfile>(`${BASE}/payroll/employees/${employeeId}/payroll-profile`, patch).then((r) => r.data);

// --- payroll-run readiness (payroll-service view of who can be paid) ---
export interface PayrollReadinessIssue { code: string; severity: string; message: string }
export interface PayrollReadinessEmployee {
  employee_id: string;
  snapshot_id: string;
  full_name: string;
  email: string;
  legal_entity_id: string;
  payroll_enabled: boolean;
  status: 'ready' | 'warning' | 'blocked' | 'excluded';
  issues: PayrollReadinessIssue[];
}
export interface PayrollReadiness {
  summary: { total: number; ready: number; warning: number; blocked: number; excluded: number };
  employees: PayrollReadinessEmployee[];
  /** Set when the report is one company's payable population for a period. */
  period?: { legal_entity_id: string; period_start: string; period_end: string };
}
/** Today's roster — or, with `period`, the people a run of that company and
 *  period would calculate (payroll's own period population, DEF-0mvn3tgtl005). */
export const getPayrollReadiness = (period?: { legalEntityId: string; periodStart: string; periodEnd: string }) =>
  gatewayApi
    .get<PayrollReadiness>(`${BASE}/payroll/readiness`, period
      ? { params: { legal_entity_id: period.legalEntityId, period_start: period.periodStart, period_end: period.periodEnd } }
      : undefined)
    .then((r) => r.data);

// --- payroll roster: bulk-set who is included in payroll (payroll_enabled) ---
// Runs (manual + scheduled) already respect payroll_enabled, so saving the roster
// once is what every future scheduled run applies. Terminated employees can't be
// re-enabled server-side — they come back in `skipped_terminated`.
export interface BulkRosterResult {
  updated: number;
  skipped_terminated: string[];
}
export const bulkSetPayrollEnabled = (
  updates: { employee_id: string; payroll_enabled: boolean }[],
) =>
  gatewayApi
    .put<BulkRosterResult>(`${BASE}/payroll/payroll-profiles/bulk`, { updates })
    .then((r) => r.data);

// --- bulk legal-entity assignment: move many employees onto one employer ---
// Only the legal entity changes; each employee's statutory IDs, classifiers and
// financials carry forward server-side, so this never wipes captured data. An
// employee with no payroll record yet comes back as skipped (not failed).
export interface BulkAssignResult {
  legal_entity_id: string;
  assigned: number;
  skipped: number;
  results: { employee_id: string; status: 'assigned' | 'skipped'; reason?: string; snapshot_version?: number }[];
}
export const assignEmployeesToLegalEntity = (legalEntityId: string, employeeIds: string[]) =>
  gatewayApi
    .post<BulkAssignResult>(`${BASE}/legal-entities/${legalEntityId}/assign-employees`, { employee_ids: employeeIds })
    .then((r) => r.data);

// --- payslip branding ---
export const getBranding = () =>
  gatewayApi.get<{ logo_url: string }>(`${BASE}/payroll/branding`).then((r) => r.data.logo_url ?? '');

export const setBranding = (logoUrl: string) =>
  gatewayApi.put(`${BASE}/payroll/branding`, { logo_url: logoUrl }).then((r) => r.data);

// Loan & advance policy — includes the min-net-pay floor (deduction protection) and
// whether advances may draw net below it.
export interface LoanPolicy {
  interest_method: string;
  annual_rate: string;
  max_advance_amount: string;
  max_loan_principal: string;
  max_term_months: number;
  min_net_pay: string;
  advances_bypass_min_net: boolean;
}

export const getLoanPolicy = () =>
  gatewayApi.get<LoanPolicy>(`${BASE}/loan-policy`).then((r) => r.data);

export const setLoanPolicy = (body: LoanPolicy) =>
  gatewayApi.put<LoanPolicy>(`${BASE}/loan-policy`, body).then((r) => r.data);

// Approval policy — the payroll approval chain plus the segregation-of-duties toggles.
//
// Phase 4 (D5/D6): a policy belongs to ONE company (legal_entity_id set) or is the
// company-group default (legal_entity_id null) that governs every company without its own.
// GET ?legal_entity_id=X returns the policy that GOVERNS X — its own, the group default, or
// the built-in — and `source` says which:
//   • 'entity'  — X has its own policy.
//   • 'tenant'  — X uses the company-group default.
//   • 'builtin' — nothing is configured; the built-in chain applies.
//
// The chain is a fixed vocabulary rendered in order HR → Finance → Group Finance → Final:
//   • hr      — optional pre-approval.
//   • finance — MANDATORY. The server forces required=true, distinct=true, min_amount=null,
//     no effective_from, and rejects any PUT that weakens it.
//   • group   — optional Group Finance stage: always scope 'group' (an approver with access
//     to every company), typically thresholded so only large runs escalate.
//   • final   — optional sign-off, may carry an amount threshold.
// Thresholds are in the company's BASE currency (no FX). A level may repeat with distinct
// effective_from dates (the latest in force at the run's period wins).
// Runs already submitted keep the policy snapshot they were submitted under.
export type ApprovalLevelKey = 'hr' | 'finance' | 'group' | 'final';
export type ApprovalLevelScope = 'entity' | 'group';
export type ApprovalPolicySource = 'builtin' | 'tenant' | 'entity';

export interface ApprovalLevel {
  key: ApprovalLevelKey;
  label: string;
  required: boolean;
  role_key: string;
  distinct: boolean;
  /** Decimal string (or number from older payloads), or null = always applies. */
  min_amount: string | number | null;
  currency?: string;
  /** YYYY-MM-DD; absent = always in force. */
  effective_from?: string;
  /** '' / 'entity' = an approver with access to the run's company; 'group' = access to every company. */
  scope?: ApprovalLevelScope | '';
  /** An entity-scope level a Group Finance approver may stand in for. */
  substitute_allowed?: boolean;
}

export interface ApprovalPolicy {
  policy_id: string | null;
  legal_entity_id: string | null;
  source: ApprovalPolicySource;
  configured: boolean;
  version: number;
  require_distinct_final_approver: boolean;
  require_distinct_releaser: boolean;
  /** The person releasing payment must be none of the run's approvers (nor its preparer). */
  require_releaser_not_approver: boolean;
  // null when this policy never customised the chain — render effective_levels
  // (the current default) so the admin sees the live config before editing.
  approval_levels: ApprovalLevel[] | null;
  effective_levels: ApprovalLevel[];
  /** The company's base currency (entity reads only) — every threshold is in it. */
  base_currency?: string;
}

// PUT body: the SoD toggles + the full replacement chain. effective_levels is GET-only.
export interface ApprovalPolicyInput {
  legal_entity_id?: string | null;
  require_distinct_final_approver: boolean;
  require_distinct_releaser: boolean;
  require_releaser_not_approver: boolean;
  approval_levels: ApprovalLevel[];
}

const policyParams = (legalEntityId?: string | null) =>
  legalEntityId ? { params: { legal_entity_id: legalEntityId } } : undefined;

/** The policy governing a company (or the company-group default when no id is given).
 *  404 = the company is outside your reach. */
export const getApprovalPolicy = (legalEntityId?: string | null) =>
  gatewayApi.get<ApprovalPolicy>(`${BASE}/approval-policy`, policyParams(legalEntityId)).then((r) => r.data);

/** Every policy row you can see: the group default (when configured) + each company that has its own. */
export const listApprovalPolicies = () =>
  gatewayApi
    .get<{ policies: ApprovalPolicy[] }>(`${BASE}/approval-policies`)
    .then((r) => r.data?.policies ?? []);

// Validation failures (e.g. weakening the mandatory Finance level) return 400 with a
// message; editing the group default without access to every company returns 403.
// Callers surface both verbatim via getApiErrorMessage.
export const setApprovalPolicy = (body: ApprovalPolicyInput) =>
  gatewayApi
    .put<ApprovalPolicy>(`${BASE}/approval-policy`, body, policyParams(body.legal_entity_id))
    .then((r) => r.data);

/** Remove a company's own policy (it inherits the group default again), or — with no id —
 *  the group default (companies fall back to the built-in chain). */
export const deleteApprovalPolicy = (legalEntityId?: string | null) =>
  gatewayApi
    .delete<{ removed: boolean }>(`${BASE}/approval-policy`, policyParams(legalEntityId))
    .then((r) => r.data);

// ---- Employee portal policy (My Pay surfaces) ----
export interface EmployeePortalSettings {
  show_compensation: boolean;
}

export const getEmployeePortalSettings = () =>
  gatewayApi.get<EmployeePortalSettings>(`${BASE}/payroll/employee-portal`).then((r) => r.data);

export const setEmployeePortalSettings = (body: EmployeePortalSettings) =>
  gatewayApi.put<EmployeePortalSettings>(`${BASE}/payroll/employee-portal`, body).then((r) => r.data);

// NOTE: Per-tenant payout provider config lives on the dedicated Payment provider
// page (settings/payments) via paygateApi → paygate-service, not here.

// ---- Inter-company cost allocation (multi-entity §12.6) ----
//
// Which share of an employee's salary cost another company in the group bears.
// AT COST, and the basis is a share of the SALARY — nothing here is derived
// from shifts, work items or assignments.
//
// The rows are the recharges AWAY from the employer; the employer keeps the
// remainder, so no rows means 100% employer. `employer_percent` is computed
// server-side so no client re-derives it and gets it subtly different.

export type CostBasis = 'employment_cost' | 'salary_only';

export interface CostAllocation {
  id: string;
  legal_entity_id: string;
  entity_name?: string;
  percent: string;
  effective_from: string;
  effective_to?: string;
  note?: string;
}

export interface CostAllocationList {
  allocations: CostAllocation[];
  employer_percent: string;
  allocated_percent: string;
  basis: CostBasis;
}

export interface CostAllocationInput {
  legal_entity_id: string;
  percent: string;
  effective_from: string;
  effective_to?: string;
  note?: string;
}

export const listCostAllocations = (employeeId: string) =>
  gatewayApi
    .get<CostAllocationList>(`${BASE}/payroll/employees/${employeeId}/cost-allocations`)
    .then((r) => r.data);

// 400 `over_allocated` when the shares would exceed 100%, 403 when the caller
// does not administer BOTH companies. Both carry a message; callers surface it
// verbatim via getApiErrorMessage.
export const createCostAllocation = (employeeId: string, body: CostAllocationInput) =>
  gatewayApi
    .post<{ id: string }>(`${BASE}/payroll/employees/${employeeId}/cost-allocations`, body)
    .then((r) => r.data);

export const revokeCostAllocation = (id: string) =>
  gatewayApi.delete(`${BASE}/payroll/cost-allocations/${id}`).then(() => undefined);

export const getCostAllocationBasis = () =>
  gatewayApi.get<{ basis: CostBasis }>(`${BASE}/payroll/cost-allocation-settings`).then((r) => r.data);

export const setCostAllocationBasis = (basis: CostBasis) =>
  gatewayApi
    .put<{ basis: CostBasis }>(`${BASE}/payroll/cost-allocation-settings`, { basis })
    .then((r) => r.data);

// ---- A run's inter-company charge ----

export interface CrossChargeEntity {
  legal_entity_id: string;
  entity_name?: string;
  employees: number;
  amount: string;
}

export interface CrossChargeJournalEntry {
  logical_code: string;
  account_code: string;
  account: string;
  dr_cr: 'DR' | 'CR';
  amount: string;
  legal_entity_id?: string;
}

export interface RunCrossCharge {
  run_id: string;
  period: string;
  currency: string;
  employing_entity_id: string;
  employing_entity_name?: string;
  basis: CostBasis;
  base: string;
  retained: string;
  charges: CrossChargeEntity[] | null;
  journal: { entries: CrossChargeJournalEntry[] | null; balanced: boolean };
}

export const getRunCrossCharge = (runId: string) =>
  gatewayApi.get<RunCrossCharge>(`${BASE}/payroll-runs/${runId}/cross-charge`).then((r) => r.data);
